import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ActiveFilterChips } from './ActiveFilterChips';
import { AppSwitcher } from './AppSwitcher';
import { DateRangePicker } from './DateRangePicker';
import { FiltersPopover } from './FiltersPopover';
import { FocusReplacedNotice } from './PageStates';
import { accountsQuery } from './api';
import { matchingPeriod } from './dates';
import { filtersOf, type FocusSearch } from './search';
import type { FocusAppScope, FocusModel, FocusScope } from './useFocus';
import { windowHint } from './window';

type FocusBarProps<S extends FocusSearch, Scope extends FocusAppScope> = {
  focus: FocusModel<S, Scope>;
  /** The resolved scope (from `FocusGate`). */
  scope: Scope;
  /** Control the Filters panel, e.g. to open it from an empty state. Uncontrolled when omitted. */
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
  /** Open the Filters panel on one field (see FiltersPopover `highlight`). */
  filtersHighlight?: 'country_code';
  /** The screen's own controls right after the scope controls (e.g. Creative lab's Ideas | Projects). */
  inline?: ReactNode;
  /** The screen's own controls at the end of the bar (e.g. Performance's Table / Board). */
  end?: ReactNode;
};

/**
 * The Focus bar: app switcher, then — as the screen's config allows — the
 * date chip, the Filters chip and the active-filter chips, then the screen's
 * own controls. The "your Focus was replaced" notice follows it.
 */
export function FocusBar<S extends FocusSearch, Scope extends FocusAppScope>({
  focus,
  scope,
  filtersOpen,
  onFiltersOpenChange,
  filtersHighlight,
  inline,
  end,
}: FocusBarProps<S, Scope>) {
  const { config, search, actions, focusApps } = focus;
  const [ownOpen, setOwnOpen] = useState(false);
  const open = filtersOpen ?? ownOpen;
  const setOpen = onFiltersOpenChange ?? setOwnOpen;
  const accounts = useQuery({ ...accountsQuery(), enabled: Boolean(config.filters) });
  const bounds = focus.bounds ?? null;
  // A screen without dates resolves a FocusAppScope (no dates to show).
  const dated: Partial<FocusScope> = scope;
  const dates = dated.dateFrom && dated.dateTo ? { from: dated.dateFrom, to: dated.dateTo } : null;
  const filters = filtersOf(search);
  const visibleFilters = config.filters && config.filters.assetTypes === false ? { ...filters, asset_type: [] } : filters;
  const apps = focusApps.data?.apps;

  return (
    <>
      <div data-tour="focus-bar" className="flex flex-wrap items-center gap-2.5 border-b border-line bg-bg px-gutter py-band">
        <AppSwitcher
          selected={search.app_id}
          portfolio={Boolean(search.all_apps)}
          allApps={config.allApps}
          copy={config.appSwitcher}
          apps={focusApps}
          onChange={actions.setApps}
        />
        {config.dates && dates ? (
          <DateRangePicker
            from={dates.from}
            to={dates.to}
            period={matchingPeriod(search.period, dates, bounds)}
            bounds={bounds}
            hint={windowHint(search.app_id, Boolean(search.all_apps), apps)}
            onChange={actions.setDates}
          />
        ) : null}
        {config.filters ? (
          <>
            <FiltersPopover
              open={open}
              onOpenChange={setOpen}
              controls={{ allApps: config.allApps, ...config.filters }}
              applied={filters}
              dates={dates}
              apps={focusApps}
              accounts={accounts}
              onApply={actions.applyFilters}
              highlight={filtersHighlight}
            />
            <ActiveFilterChips filters={visibleFilters} accounts={accounts.data ?? []} onRemove={actions.removeFilters} />
          </>
        ) : null}
        {inline}
        {end ? <div className="ml-auto flex flex-wrap items-center gap-2">{end}</div> : null}
      </div>
      <FocusReplacedNotice message={focus.notice.message} onDismiss={focus.notice.dismiss} />
    </>
  );
}
